import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { Spin } from 'antd';
import styled from 'styled-components';

import { CareerIcon, TrackIcon } from 'atoms';
import { Colors } from 'context';
import { IProfession, useProfessions } from 'core';
import { ISkillGap } from 'core/hooks/Growth/transform/transformSkillProgress';
import { CareerLinkCard } from 'molecules/CareerLinkCard';
import { professionHref } from 'router/careerLinks';
import { careerType } from 'services/careerType';
import { tablet } from 'services/ui';

type Translate = (key: string, vars?: Record<string, unknown>) => string;

/**
 * Каталог профессий (ТЗ 0071, п. 7.1).
 *
 * Проверка в Brave: карточка несла облако из 20–24 тегов навыков, «Nasze
 * kursy», вилку, шаги и три кнопки — сравнивать профессии между собой по такой
 * карточке было невозможно. Теперь в ней только то, по чему выбирают:
 *
 *   иконка · название · чем занимается · соответствие с подписью ·
 *   три ближайших пробела · вакансии · «Poznaj zawód»
 *
 * Карточка — ОДНА ссылка на страницу профессии. Выбор цели переехал туда же, за
 * подтверждение: смена цели перестраивает карту и рекомендации, её не делают
 * случайным кликом по карточке каталога.
 *
 * Числа — из `skillProgress` сервера, той же функции, что на главной, в
 * паспорте и на карте.
 */
export const GrowthProfessions = memo(() => {
  const { t } = useTranslation();
  const { professions, loading } = useProfessions();

  if (loading && !professions.length) {
    return (
      <Center>
        <Spin />
      </Center>
    );
  }
  if (!professions.length) return <Muted>{t('growth.professions.empty')}</Muted>;

  return (
    <List>
      {professions.map((row) => (
        <li key={row.code}>
          <ProfessionCard profession={row} />
        </li>
      ))}
    </List>
  );
});

const gapText = (t: Translate, gap: ISkillGap) =>
  gap.status === 'improve' && gap.myLevel !== null
    ? t('growth.profession.chipImprove', { mine: gap.myLevel, required: gap.requiredLevel })
    : t('growth.profession.chipRequired', { required: gap.requiredLevel });

const ProfessionCard = memo(({ profession }: { profession: IProfession }) => {
  const { t } = useTranslation();
  const progress = profession.skillProgress;

  return (
    <CareerLinkCard
      to={professionHref(profession.code)}
      tone={profession.isChosen ? 'current' : 'default'}>
      <Card>
        <Head>
          <TrackIcon icon={profession.icon} size={44} />
          <HeadText>
            <Title>{profession.title}</Title>
            {profession.isChosen ? (
              <Goal>
                <CareerIcon name="goal" size={16} />
                {t('growth.professions.yourGoal')}
              </Goal>
            ) : null}
          </HeadText>
        </Head>

        {profession.summary ? <Summary>{profession.summary}</Summary> : null}

        {profession.hasRequirements && progress.requiredCount > 0 ? (
          <Fit>
            <Bar aria-hidden>
              <BarFill
                style={{
                  width: `${Math.round((progress.meetsCount / progress.requiredCount) * 100)}%`,
                }}
              />
            </Bar>
            {t('careerCenter.skillProgress.meets', {
              meets: progress.meetsCount,
              total: progress.requiredCount,
            })}
          </Fit>
        ) : (
          <Muted>{t('growth.professions.noRequirements')}</Muted>
        )}

        {profession.topGaps.length ? (
          <Gaps>
            <GapsTitle>{t('growth.professions.nearestGaps')}</GapsTitle>
            <GapList>
              {profession.topGaps.map((gap) => (
                <li key={gap.slug}>
                  <GapName>{gap.name}</GapName> <GapLevel>{gapText(t, gap)}</GapLevel>
                </li>
              ))}
            </GapList>
          </Gaps>
        ) : null}

        <Footer>
          {profession.hasRequirements ? (
            <Meta>{t('growth.professions.vacancies', { n: profession.liveVacancies })}</Meta>
          ) : null}
          <More>{t('growth.professions.explore')}</More>
        </Footer>
      </Card>
    </CareerLinkCard>
  );
});

const List = styled.ul`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 16px;
  margin: 0;
  padding: 0;
  list-style: none;

  ${tablet} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  & > li {
    display: flex;
  }
`;

const Card = styled.span`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Head = styled.span`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const HeadText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const Title = styled.span`
  ${careerType.h3()};
`;

const Goal = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  ${careerType.metaStrong()};
`;

const Summary = styled.span`
  color: ${Colors.gray_dark};
  ${careerType.body()};
`;

const Fit = styled.span`
  display: flex;
  flex-direction: column;
  gap: 6px;
  ${careerType.bodyStrong()};
`;

const Bar = styled.span`
  display: block;
  height: 8px;
  border-radius: 999px;
  background: ${Colors.bg_gray};
  overflow: hidden;
`;

const BarFill = styled.span`
  display: block;
  height: 100%;
  border-radius: 999px;
  background: ${Colors.signal_green};
`;

const Gaps = styled.span`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const GapsTitle = styled.span`
  color: ${Colors.gray_dark};
  ${careerType.metaStrong()};
`;

const GapList = styled.ul`
  margin: 0;
  padding: 0 0 0 20px;
  ${careerType.body()};
`;

const GapName = styled.span`
  font-weight: 500;
`;

const GapLevel = styled.span`
  color: ${Colors.gray_dark};
  ${careerType.meta()};
`;

const Footer = styled.span`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 4px;
`;

const Meta = styled.span`
  color: ${Colors.gray_dark};
  ${careerType.meta()};
`;

const More = styled.span`
  text-decoration: underline;
  ${careerType.bodyStrong()};
`;

const Center = styled.div`
  display: flex;
  justify-content: center;
  padding: 40px 0;
`;

const Muted = styled.span`
  color: ${Colors.gray_dark};
  ${careerType.meta()};
`;
